<template>
  <div class="contact-page min-h-screen flex flex-col">
    <!-- Popup Modal -->
    <PopupModal
      :is-visible="popup.isVisible"
      :type="popup.type"
      :title="popup.title"
      :message="popup.message"
      :confirm-text="popup.confirmText"
      @close="closePopup"
      @confirm="closePopup"
    />

    <!-- Hero -->
    <section class="relative w-full h-[350px] flex items-center justify-center">
  <!-- Background image -->
  <img
    src="@/assets/ContactUs/contact_us.jpg"
    alt="Contact Us Hero"
    class="absolute inset-0 w-full h-full object-cover object-center z-0 block"
  />

  <!-- Strong hero overlay (same as menu hero) -->
  <div class="absolute inset-0 z-10 contact-hero-overlay pointer-events-none"></div>

  <!-- Content -->
  <div class="relative z-20 text-center px-6">
    <h1 class="text-2xl sm:text-3xl md:text-3xl lg:text-4xl font-['Cormorant_SC'] font-bold mb-4 mt-12 contact-hero-title">
      {{ $t('contact.title') }}
    </h1>
    <p class="text-base sm:text-lg md:text-xl lg:text-2xl max-w-xl mx-auto contact-hero-subtitle">
      {{ $t('contact.subtitle') }}
    </p>
  </div>
</section>

    <!-- Main Content -->
    <section
      class="contact-section flex flex-col md:flex-row gap-12 max-w-5xl mx-auto rounded-2xl mt-8 mb-16 p-8 z-0 relative"
      :class="{ 'flex-row-reverse': $i18n.locale === 'ar' }"
    >
      <!-- Left: Info -->
      <div class="flex-1 flex flex-col gap-6 justify-center">
        <h2
          class="font-bold text-base sm:text-lg md:text-xl lg:text-2xl mb-2 contact-text"
        >
          {{ $t("contact.letsTalk") }}
        </h2>
        <p
          class="contact-muted text-xs sm:text-sm md:text-base lg:text-lg mb-4"
        >
          {{ $t("contact.description") }}
        </p>

        <!-- Working Hours -->
        <div
          class="flex items-start gap-4 w-full contact-text"
          :class="{
            'flex-row-reverse text-right justify-end': $i18n.locale === 'ar',
          }"
        >
          <template v-if="$i18n.locale !== 'ar'">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="28"
              height="28"
              viewBox="0 0 33 33"
              fill="none"
              class="mt-1 flex-shrink-0"
            >
              <path
                d="M16.5003 3.42676C14.7494 3.42676 13.0156 3.77163 11.3979 4.4417C9.7802 5.11176 8.31035 6.09389 7.07223 7.332C4.57175 9.83249 3.16699 13.2239 3.16699 16.7601C3.16699 20.2963 4.57175 23.6877 7.07223 26.1882C8.31035 27.4263 9.7802 28.4084 11.3979 29.0785C13.0156 29.7485 14.7494 30.0934 16.5003 30.0934C20.0365 30.0934 23.4279 28.6887 25.9284 26.1882C28.4289 23.6877 29.8337 20.2963 29.8337 16.7601C29.8337 15.0091 29.4888 13.2753 28.8187 11.6576C28.1487 10.04 27.1665 8.57012 25.9284 7.332C24.6903 6.09389 23.2204 5.11176 21.6028 4.4417C19.9851 3.77163 18.2513 3.42676 16.5003 3.42676ZM22.1003 22.3601L15.167 18.0934V10.0934H17.167V17.0268L23.167 20.6268L22.1003 22.3601Z"
                fill="var(--primary)"
              />
            </svg>
            <div>
              <span
                class="font-semibold text-base sm:text-lg md:text-xl lg:text-2xl"
                >{{ $t("contact.workingHours") }}</span
              ><br />
              <span
                class="contact-muted text-xs sm:text-sm md:text-base lg:text-lg"
                >{{ $t("contact.workingTime") }}</span
              >
            </div>
          </template>
          <template v-else>
            <div>
              <span
                class="font-semibold text-base sm:text-lg md:text-xl lg:text-2xl"
                >{{ $t("contact.workingHours") }}</span
              ><br />
              <span
                class="contact-muted text-xs sm:text-sm md:text-base lg:text-lg"
                >{{ $t("contact.workingTime") }}</span
              >
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="28"
              height="28"
              viewBox="0 0 33 33"
              fill="none"
              class="mt-1 flex-shrink-0"
            >
              <path
                d="M16.5003 3.42676C14.7494 3.42676 13.0156 3.77163 11.3979 4.4417C9.7802 5.11176 8.31035 6.09389 7.07223 7.332C4.57175 9.83249 3.16699 13.2239 3.16699 16.7601C3.16699 20.2963 4.57175 23.6877 7.07223 26.1882C8.31035 27.4263 9.7802 28.4084 11.3979 29.0785C13.0156 29.7485 14.7494 30.0934 16.5003 30.0934C20.0365 30.0934 23.4279 28.6887 25.9284 26.1882C28.4289 23.6877 29.8337 20.2963 29.8337 16.7601C29.8337 15.0091 29.4888 13.2753 28.8187 11.6576C28.1487 10.04 27.1665 8.57012 25.9284 7.332C24.6903 6.09389 23.2204 5.11176 21.6028 4.4417C19.9851 3.77163 18.2513 3.42676 16.5003 3.42676ZM22.1003 22.3601L15.167 18.0934V10.0934H17.167V17.0268L23.167 20.6268L22.1003 22.3601Z"
                fill="var(--primary)"
              />
            </svg>
          </template>
        </div>

        <!-- Address -->
        <div
          class="flex items-start gap-4 w-full contact-text"
          :class="{
            'flex-row-reverse text-right justify-end': $i18n.locale === 'ar',
          }"
        >
          <template v-if="$i18n.locale !== 'ar'">
            <svg
              width="28"
              height="28"
              fill="none"
              viewBox="0 0 35 35"
              class="flex-shrink-0"
            >
              <path
                d="M26.7808 25.3223L17.5 34.603L8.21923 25.3223C3.09359 20.1966 3.09359 11.8864 8.21923 6.76073C13.3449 1.6351 21.6551 1.6351 26.7808 6.76073C31.9064 11.8864 31.9064 20.1966 26.7808 25.3223ZM17.5 21.8748C20.7216 21.8748 23.3333 19.2631 23.3333 16.0415C23.3333 12.8198 20.7216 10.2082 17.5 10.2082C14.2783 10.2082 11.6667 12.8198 11.6667 16.0415C11.6667 19.2631 14.2783 21.8748 17.5 21.8748ZM17.5 18.9582C15.8891 18.9582 14.5833 17.6524 14.5833 16.0415C14.5833 14.4307 15.8891 13.1248 17.5 13.1248C19.1109 13.1248 20.4167 14.4307 20.4167 16.0415C20.4167 17.6524 19.1109 18.9582 17.5 18.9582Z"
                fill="var(--primary)"
              />
            </svg>
            <div>
              <span
                class="font-semibold text-base sm:text-lg md:text-xl lg:text-2xl"
                v-html="$t('contact.address')"
              ></span>
            </div>
          </template>
          <template v-else>
            <div>
              <span
                class="font-semibold text-base sm:text-lg md:text-xl lg:text-2xl"
                v-html="$t('contact.address')"
              ></span>
            </div>
            <svg
              width="28"
              height="28"
              fill="none"
              viewBox="0 0 35 35"
              class="flex-shrink-0"
            >
              <path
                d="M26.7808 25.3223L17.5 34.603L8.21923 25.3223C3.09359 20.1966 3.09359 11.8864 8.21923 6.76073C13.3449 1.6351 21.6551 1.6351 26.7808 6.76073C31.9064 11.8864 31.9064 20.1966 26.7808 25.3223ZM17.5 21.8748C20.7216 21.8748 23.3333 19.2631 23.3333 16.0415C23.3333 12.8198 20.7216 10.2082 17.5 10.2082C14.2783 10.2082 11.6667 12.8198 11.6667 16.0415C11.6667 19.2631 14.2783 21.8748 17.5 21.8748ZM17.5 18.9582C15.8891 18.9582 14.5833 17.6524 14.5833 16.0415C14.5833 14.4307 15.8891 13.1248 17.5 13.1248C19.1109 13.1248 20.4167 14.4307 20.4167 16.0415C20.4167 17.6524 19.1109 18.9582 17.5 18.9582Z"
                fill="var(--primary)"
              />
            </svg>
          </template>
        </div>

        <!-- Call Us -->
        <div
          class="flex items-start gap-4 w-full contact-text"
          :class="{
            'flex-row-reverse text-right justify-end': $i18n.locale === 'ar',
          }"
        >
          <template v-if="$i18n.locale !== 'ar'">
            <svg
              width="28"
              height="28"
              fill="none"
              viewBox="0 0 35 35"
              class="flex-shrink-0"
            >
              <path
                d="M30.625 23.9458V29.1026C30.625 29.8683 30.0331 30.5035 29.2695 30.5575C28.6316 30.6025 28.1113 30.625 27.7083 30.625C14.8216 30.625 4.375 20.1784 4.375 7.29167C4.375 6.88874 4.39753 6.36835 4.44259 5.73052C4.49658 4.96691 5.13179 4.375 5.8973 4.375H11.0543C11.4286 4.375 11.7422 4.65853 11.7797 5.03096C11.8135 5.36531 11.8448 5.63375 11.8739 5.83628C12.1688 7.89647 12.7714 9.8574 13.6335 11.6711C13.7719 11.9622 13.6817 12.3107 13.4194 12.498L10.2718 14.7464C12.1881 19.2224 15.7776 22.812 20.2536 24.7282L22.4979 21.5861C22.6876 21.3207 23.0402 21.2294 23.3348 21.3693C25.1484 22.2307 27.1091 22.8325 29.169 23.1268C29.3703 23.1557 29.637 23.1868 29.969 23.2203C30.3415 23.2579 30.625 23.5715 30.625 23.9458Z"
                fill="var(--primary)"
              />
            </svg>
            <div>
              <span
                class="font-semibold text-base sm:text-lg md:text-xl lg:text-2xl"
                >{{ $t("contact.callUs") }}</span
              ><br />
              <span
                class="contact-muted text-xs sm:text-sm md:text-base lg:text-lg"
                >{{ $t("contact.phone") }}</span
              >
            </div>
          </template>
          <template v-else>
            <div>
              <span
                class="font-semibold text-base sm:text-lg md:text-xl lg:text-2xl"
                >{{ $t("contact.callUs") }}</span
              ><br />
              <span
                class="contact-muted text-xs sm:text-sm md:text-base lg:text-lg"
                >{{ $t("contact.phone") }}</span
              >
            </div>
            <svg
              width="28"
              height="28"
              fill="none"
              viewBox="0 0 35 35"
              class="flex-shrink-0"
            >
              <path
                d="M30.625 23.9458V29.1026C30.625 29.8683 30.0331 30.5035 29.2695 30.5575C28.6316 30.6025 28.1113 30.625 27.7083 30.625C14.8216 30.625 4.375 20.1784 4.375 7.29167C4.375 6.88874 4.39753 6.36835 4.44259 5.73052C4.49658 4.96691 5.13179 4.375 5.8973 4.375H11.0543C11.4286 4.375 11.7422 4.65853 11.7797 5.03096C11.8135 5.36531 11.8448 5.63375 11.8739 5.83628C12.1688 7.89647 12.7714 9.8574 13.6335 11.6711C13.7719 11.9622 13.6817 12.3107 13.4194 12.498L10.2718 14.7464C12.1881 19.2224 15.7776 22.812 20.2536 24.7282L22.4979 21.5861C22.6876 21.3207 23.0402 21.2294 23.3348 21.3693C25.1484 22.2307 27.1091 22.8325 29.169 23.1268C29.3703 23.1557 29.637 23.1868 29.969 23.2203C30.3415 23.2579 30.625 23.5715 30.625 23.9458Z"
                fill="var(--primary)"
              />
            </svg>
          </template>
        </div>

        <!-- Email -->
        <div
          class="flex items-start gap-4 w-full contact-text"
          :class="{
            'flex-row-reverse text-right justify-end': $i18n.locale === 'ar',
          }"
        >
          <template v-if="$i18n.locale !== 'ar'">
            <svg
              width="28"
              height="28"
              fill="none"
              viewBox="0 0 35 35"
              class="flex-shrink-0"
            >
              <path
                d="M29.1667 5.8335H5.83335C4.22919 5.8335 2.93127 7.146 2.93127 8.75016L2.91669 26.2502C2.91669 27.8543 4.22919 29.1668 5.83335 29.1668H29.1667C30.7709 29.1668 32.0834 27.8543 32.0834 26.2502V8.75016C32.0834 7.146 30.7709 5.8335 29.1667 5.8335ZM29.1667 11.6668L17.5 18.9585L5.83335 11.6668V8.75016L17.5 16.0418L29.1667 8.75016V11.6668Z"
                fill="var(--primary)"
              />
            </svg>
            <div>
              <span
                class="font-semibold text-base sm:text-lg md:text-xl lg:text-2xl"
                >{{ $t("contact.email") }}</span
              ><br />
              <span
                class="contact-muted text-xs sm:text-sm md:text-base lg:text-lg"
                >tuscany.house@gmail.com</span
              >
            </div>
          </template>
          <template v-else>
            <div>
              <span
                class="font-semibold text-base sm:text-lg md:text-xl lg:text-2xl"
                >{{ $t("contact.email") }}</span
              ><br />
              <span
                class="contact-muted text-xs sm:text-sm md:text-base lg:text-lg"
                >tuscany.house@gmail.com</span
              >
            </div>
            <svg
              width="28"
              height="28"
              fill="none"
              viewBox="0 0 35 35"
              class="flex-shrink-0"
            >
              <path
                d="M29.1667 5.8335H5.83335C4.22919 5.8335 2.93127 7.146 2.93127 8.75016L2.91669 26.2502C2.91669 27.8543 4.22919 29.1668 5.83335 29.1668H29.1667C30.7709 29.1668 32.0834 27.8543 32.0834 26.2502V8.75016C32.0834 7.146 30.7709 5.8335 29.1667 5.8335ZM29.1667 11.6668L17.5 18.9585L5.83335 11.6668V8.75016L17.5 16.0418L29.1667 8.75016V11.6668Z"
                fill="var(--primary)"
              />
            </svg>
          </template>
        </div>
      </div>

      <!-- Right: Form -->
      <form
        @submit="handleSubmit"
        class="contact-card flex-1 rounded-2xl p-8 flex flex-col gap-6 max-w-lg mx-auto"
      >
        <input
          v-model="form.name"
          type="text"
          :placeholder="$t('contact.form.name')"
          class="contact-input"
        />
        <input
          v-model="form.email"
          type="email"
          :placeholder="$t('contact.form.email')"
          class="contact-input"
        />
        <input
          v-model="form.phone"
          type="tel"
          :placeholder="$t('contact.form.phone')"
          :class="[
            $i18n.locale === 'ar' ? 'text-right' : 'text-left',
            'contact-input',
          ]"
          :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'"
        />
        <textarea
          v-model="form.message"
          :placeholder="$t('contact.form.message')"
          rows="4"
          class="contact-input resize-none"
        ></textarea>

        <button
          type="submit"
          :disabled="loading.submitting"
          class="contact-cta disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {{
            loading.submitting
              ? $t("contact.form.sending")
              : $t("contact.form.sendMessage")
          }}
        </button>
      </form>
    </section>

    <SmartChatbot />
  </div>
</template>

<script setup>
import { reactive, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  getFirestore,
  doc,
  getDoc,
  addDoc,
  collection,
  serverTimestamp,
  deleteDoc,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import PopupModal from "@/components/PopupModal.vue";

const { t } = useI18n();

const auth = getAuth();
const db = getFirestore();

const form = reactive({
  name: "",
  email: "",
  phone: "",
  message: "",
});

const loading = reactive({
  submitting: false,
});

const popup = reactive({
  isVisible: false,
  type: "info",
  title: "Notification",
  message: "",
  confirmText: t("contact.form.ok"), // Use translation key for OK button
});

let authUnsubscribe = null;

onMounted(() => {
  authUnsubscribe = onAuthStateChanged(auth, async (user) => {
    if (user) {
      try {
        const userDocRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          form.name = userData.username || "";
          form.email = userData.email || "";
          form.phone = userData.phone || "";
        }
      } catch (e) {
        console.error("Error fetching user data:", e);
      }
    } else {
      form.name = "";
      form.email = "";
      form.phone = "";
      form.message = "";
    }
  });
});

onUnmounted(() => {
  if (authUnsubscribe) authUnsubscribe();
});

const showPopup = (type, title, message, confirmText = t("contact.form.ok")) => {
  popup.type = type;
  popup.title = title;
  popup.message = message;
  popup.confirmText = confirmText;
  popup.isVisible = true;
};

const closePopup = async () => {
  popup.isVisible = false;
  // After success, delete the last sent message from Firestore
  if (popup.type === "success") {
    try {
      // Find the last message sent by this user (by email and message)
      const q = query(
        collection(db, "contact_messages"),
        where("email", "==", form.email),
        where("message", "==", form.message)
      );
      const querySnapshot = await getDocs(q);
      for (const docSnap of querySnapshot.docs) {
        await deleteDoc(doc(db, "contact_messages", docSnap.id));
      }
      // Optionally clear the form
      form.message = "";
    } catch (error) {
      console.error("Error deleting sent message:", error);
    }
  }
};

const validateForm = () => {
  const errors = [];
  if (!form.name.trim()) errors.push(t("contact.validation.nameRequired"));
  if (!form.email.trim()) errors.push(t("contact.validation.emailRequired"));
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
    errors.push(t("contact.validation.invalidEmail"));
  if (!form.phone.trim()) errors.push(t("contact.validation.phoneRequired"));
  if (!form.message.trim())
    errors.push(t("contact.validation.messageRequired"));
  else if (form.message.trim().length < 10)
    errors.push(t("contact.validation.messageTooShort"));
  return errors;
};

const handleSubmit = async (e) => {
  e.preventDefault();
  const errors = validateForm();
  if (errors.length) {
    showPopup(
      "error",
      t("contact.error.validation"),
      "Please fix:\n" + errors.join("\n")
    );
    return;
  }

  loading.submitting = true;
  try {
    await addDoc(collection(db, "contact_messages"), {
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      message: form.message.trim(),
      createdAt: serverTimestamp(),
      status: "unread",
    });
    showPopup(
      "success",
      t("contact.success.title"),
      t("contact.success.message"),
      t("contact.form.ok") // Use translation for OK button
    );
  } catch (error) {
    console.error("Error sending message:", error);
    let msg = t("contact.error.general");
    if (error.code === "permission-denied") msg = t("contact.error.permission");
    else if (error.code === "unavailable") msg = t("contact.error.network");
    else if (error.code === "resource-exhausted")
      msg = t("contact.error.service");
    showPopup("error", t("contact.error.title"), msg, t("contact.form.ok"));
  } finally {
    loading.submitting = false;
  }
};
</script>

<style scoped>
/* Page bg + base text */
.contact-page {
  background: var(--contact-bg);
  color: var(--contact-text);
}

/* Hero overlay + texts */
.contact-hero-overlay {
  background: var(--contact-hero-overlay);
}
.contact-hero-title {
  color: var(--contact-hero-title);
}
.contact-hero-subtitle {
  color: var(--contact-hero-subtitle);
}

/* Content section container */
.contact-section {
  background: var(--contact-section-bg);
  box-shadow: var(--contact-card-shadow);
  border: 1px solid var(--contact-card-border);
}

/* Card (form) */
.contact-card {
  background: var(--contact-card-bg);
  border: 1px solid var(--contact-card-border);
  box-shadow: var(--contact-card-shadow);
}

/* Text helpers */
.contact-text {
  color: var(--contact-text);
}
.contact-muted {
  color: var(--contact-muted);
}

/* Inputs */
.contact-input {
  background: var(--contact-input-bg);
  color: var(--contact-input-text);
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid var(--contact-input-border);
  font-size: clamp(1rem, 2vw, 1.25rem);
  outline: none;
}
.contact-input:focus {
  border-color: var(--contact-input-focus);
  box-shadow: 0 0 0 3px rgba(245, 190, 50, 0.2);
}

/* CTA button */
.contact-cta {
  background: var(--contact-cta-bg);
  color: var(--contact-cta-text);
  font-weight: 700;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  transition: background 0.15s ease, transform 0.1s ease;
  font-size: clamp(1rem, 2vw, 1.25rem);
}
.contact-cta:hover {
  background: var(--contact-cta-hover-bg);
  transform: translateY(-1px);
}
</style>
